'use client';

import React, { useState } from 'react';
import { Plus, BarChart2, List } from 'lucide-react';
import GrowthSummaryCards from './GrowthSummaryCards';
import FlockGrowthTable from './FlockGrowthTable';
import WeightSampleForm from './WeightSampleForm';

const TABS = [
  { id: 'flocks', label: 'Flock Growth Records', icon: List },
  { id: 'analytics', label: 'Analytics', icon: BarChart2 },
];

export default function GrowthContent() {
  const [activeTab, setActiveTab] = useState('flocks');
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  function handleSave(data: Record<string, unknown>) {
    setShowForm(false);
    setToast(`Weight record saved — ${data.flockNumber} · Age ${data.ageDays}d · Avg ${Number(data.avgWeightKg).toFixed(3)} kg`);
    setTimeout(() => setToast(null), 5000);
  }

  return (
    <div className="p-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <WeightSampleForm
          onSave={handleSave}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Growth & Weight</h1>
          <p className="text-sm text-gray-500 mt-1">
            Record weight samples per flock, track actual vs target growth curves, and monitor ADG, FCR, and uniformity across all batches.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-sm flex-shrink-0"
        >
          <Plus size={16} />
          Record Weight Sample
        </button>
      </div>

      {/* Summary KPI Cards */}
      <GrowthSummaryCards />

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                isActive ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <TabIcon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'flocks' && <FlockGrowthTable />}

      {activeTab === 'analytics' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <BarChart2 size={24} className="text-blue-500" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 mb-1">Analytics Coming Soon</h3>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            Cross-flock weight comparison, breed performance benchmarks, and FCR trend analysis will appear here.
          </p>
        </div>
      )}
    </div>
  );
}
